import { useState } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Button, Empty, ErrorBlock, Input, Price, Spinner, fen } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { orgFetch } from "../../api/org-client.ts";
import { useIdentities } from "../../api/queries.ts";
import { STATUS_LABEL, type Balance, type Quote, type WithdrawalRow } from "../withdraw.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 提现
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **左边填申请，右边是记录，一屏能同时看到。**
 *    手机上要先填完、提交、再往下滚才能看到"上一笔提到哪一步了" ——
 *    而"上一笔还没打款"恰恰是他决定要不要再提一笔的依据。
 *
 * 🔴 **申请前就要告诉他到手多少。**
 *    平台向自然人支付劳务报酬有法定代扣代缴义务，但"不解释就扣"是产品事故：
 *    他申请 ¥1000 收到 ¥840，只会以为平台吞了钱。
 */
export function DesktopWithdraw() {
  const search = useSearch({ from: "/withdraw" }) as { orgId?: number };
  const { data: identities, isPending } = useIdentities();
  const qc = useQueryClient();

  const orgs = identities?.orgs ?? [];
  const orgId = search.orgId ?? orgs[0]?.id;

  const balance = useQuery({
    queryKey: ["merchant", orgId, "balance"],
    queryFn: () => orgFetch<Balance>("/balance", orgId!),
    enabled: Boolean(orgId),
  });
  /** ⚠️ 接口直接回数组，不是 `{items}` —— 包一层会拿到 undefined。 */
  const history = useQuery({
    queryKey: ["merchant", orgId, "withdrawals"],
    queryFn: () => orgFetch<WithdrawalRow[]>("/withdrawals", orgId!),
    enabled: Boolean(orgId),
  });

  const [amount, setAmount] = useState("");
  const [payeeName, setPayeeName] = useState("");
  const [payeeAccount, setPayeeAccount] = useState("");

  /** ⚠️ 输入的是元，接口要分 —— 换算只做一次，别在几处各转一遍。 */
  const cents = Math.round(Number(amount || 0) * 100);
  const withdrawable = balance.data?.withdrawable ?? 0;
  /**
   * ⚠️ 超过可提现余额就别去试算了。
   *    服务端当然会拒，但试算会先回来一个按天文数字算出的税额，
   *    满屏 ¥1991046795054724.80 比一句"余额不足"吓人得多。
   */
  const tooMuch = cents > withdrawable;

  /**
   * 试算代扣。⚠️ 金额变了就重算 —— 劳务报酬是**超额累进**，
   *    金额跨档时税率会跳，按固定比例心算是对不上的。
   */
  const quote = useQuery({
    queryKey: ["withdraw-quote", orgId, cents],
    queryFn: () => orgFetch<Quote>(`/withdrawals/quote?amount=${cents}`, orgId!),
    enabled: Boolean(orgId) && cents > 0 && !tooMuch,
  });

  const submit = useMutation({
    mutationFn: () =>
      orgFetch("/withdrawals", orgId!, {
        method: "POST",
        body: JSON.stringify({
          amount: cents,
          payee: { kind: "bank", name: payeeName, account: payeeAccount },
        }),
      }),
    onSuccess: () => {
      setAmount("");
      void qc.invalidateQueries({ queryKey: ["merchant", orgId] });
    },
  });

  /**
   * 🔴 **没有商家身份时不能只转圈。** disabled 的查询 `isPending` 恒为 true ——
   *    未登录、或名下没有店的人会看到一个永远停不下来的圈。
   */
  if (!isPending && !orgId)
    return (
      <AccountShell title="提现">
        <Empty title="你还不是商家" hint="提现是商家功能。想开店请联系上级商家或平台。" action={{ label: "回首页", to: "/" }} compact />
      </AccountShell>
    );
  if (isPending || balance.isPending)
    return (
      <AccountShell title="提现">
        <Spinner compact />
      </AccountShell>
    );
  if (balance.error)
    return (
      <AccountShell title="提现">
        <ErrorBlock error={balance.error} />
      </AccountShell>
    );

  return (
    <AccountShell title="提现">
      <div className="grid grid-cols-[minmax(0,420px)_minmax(0,1fr)] items-start gap-8">
        {/* ══════════ 左：申请 ══════════ */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card px-5 py-5">
            <p className="text-[13px] text-muted-foreground">可提现</p>
            <p className="mt-1.5">
              <Price cents={withdrawable} className="text-[32px] text-brand-600" />
            </p>
            {(balance.data?.frozen ?? 0) > 0 && (
              <p className="mt-2 text-[13px] text-muted-foreground">
                另有 <Price cents={balance.data!.frozen} /> 提现中
              </p>
            )}
            {/* ⚠️ 光有数字不够 —— 他要能查到每一笔从哪来、为什么被扣。 */}
            <Link
              to="/earnings"
              search={{ orgId } as never}
              className="mt-2.5 inline-block text-[13px] text-brand-600 transition-colors hover:text-brand-700"
            >
              查看每一笔明细 →
            </Link>
          </div>

          {withdrawable <= 0 ? (
            <div className="rounded-xl border border-border bg-card px-5 py-4 text-[14px] leading-relaxed text-muted-foreground">
              账上还没有可提现的钱。
              {/*
                ⚠️ 说清楚待结算为什么不能提 —— 不解释的话商家会以为系统扣了他的钱。
                   结算时点压到"确认收货 + 15 天"是为了覆盖售后维权期。
              */}
              <br />
              待结算的钱在售后期内（确认收货 + 15 天）不能提现。
            </div>
          ) : (
            <div className="space-y-3.5 rounded-xl border border-border bg-card p-5">
              <p className="text-[15px] font-medium">申请提现</p>
              <Input
                label="提现金额（元）"
                inputMode="decimal"
                placeholder={`最多 ${(withdrawable / 100).toFixed(2)}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                error={
                  tooMuch ? `超过可提现余额 ${fen(withdrawable)}` : undefined
                }
              />

              {/*
                🔴 **代扣要在申请前说清楚。**
                ⚠️ 企业不代扣（他们开票自行申报），所以税额为 0 时这一块不出现。
              */}
              {!tooMuch && quote.data && quote.data.taxAmount > 0 && (
                <div className="rounded-lg bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-800">
                  <p>
                    需代扣个税 <Price cents={quote.data.taxAmount} />，预计到手{" "}
                    <Price cents={quote.data.actualEstimate} />
                  </p>
                  <p className="mt-0.5 text-amber-700">{quote.data.taxSummary}</p>
                  <p className="mt-0.5">平台按规定替你预扣预缴，年度汇算时多退少补。</p>
                </div>
              )}

              <Input
                label="收款人姓名"
                value={payeeName}
                onChange={(e) => setPayeeName(e.target.value)}
              />
              <Input
                label="银行卡号"
                inputMode="numeric"
                value={payeeAccount}
                onChange={(e) => setPayeeAccount(e.target.value)}
              />

              {submit.error && <ErrorBlock error={submit.error} />}
              {submit.isSuccess && (
                <p className="text-[13px] text-emerald-700">已提交，等待平台审核打款。</p>
              )}
              <Button
                className="w-full"
                loading={submit.isPending}
                disabled={!cents || tooMuch || !payeeName.trim() || !payeeAccount.trim()}
                onClick={() => submit.mutate()}
              >
                提交申请
              </Button>
            </div>
          )}
        </div>

        {/* ══════════ 右：记录 ══════════ */}
        <div>
          <p className="mb-3 text-[15px] font-medium">提现记录</p>
          {(history.data?.length ?? 0) === 0 ? (
            <p className="rounded-xl border border-border bg-card px-5 py-4 text-[14px] text-muted-foreground">
              还没有提现记录。
            </p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="grid grid-cols-[120px_120px_120px_minmax(0,1fr)] gap-4 bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
                <span>申请金额</span>
                <span>代扣个税</span>
                <span>到手</span>
                <span>状态 / 时间</span>
              </div>
              {history.data!.map((w) => (
                <div
                  key={w.id}
                  className="grid grid-cols-[120px_120px_120px_minmax(0,1fr)] items-start gap-4 border-t border-border px-5 py-3 text-[14px]"
                >
                  <Price cents={w.amount} />
                  {/* ⚠️ 扣了税就要说到手多少 —— 不说的话他会以为平台吞了钱。 */}
                  <span className="text-muted-foreground">
                    {w.taxAmount > 0 ? <Price cents={w.taxAmount} /> : "—"}
                  </span>
                  <span>{w.actualAmount > 0 ? <Price cents={w.actualAmount} /> : "—"}</span>
                  <span className="min-w-0">
                    <span
                      className={cn(
                        "text-[13px]",
                        w.status === "rejected" ? "text-destructive" : "text-muted-foreground",
                      )}
                    >
                      {STATUS_LABEL[w.status] ?? w.status}
                    </span>
                    <span className="mt-0.5 block text-[12px] text-muted-foreground">
                      {new Date(w.createdAt).toLocaleString("zh-CN", { hour12: false })}
                    </span>
                    {/* ⚠️ 驳回/失败原因必须显示 —— 只说"已驳回"的话他不知道该怎么改。 */}
                    {(w.rejectReason || w.failReason) && (
                      <span className="mt-1 block text-[12px] text-destructive">
                        原因：{w.rejectReason || w.failReason}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AccountShell>
  );
}
